import { Box, Flex, Grid, Heading, Spinner, VStack } from '@chakra-ui/react';
import { GameCard } from 'components/GameCard/GameCard';
import { FormattedMessage } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import { useUpcomingMatchesQuery } from './hooks';
import { useEventTracking } from 'hooks/useEventTracking';

interface MatchData {
  eventKey: string;
  eventId: number;
  title?: string;
  date?: string | null;
  stadium?: string | null;
  teamA?: { name: string };
  teamB?: { name: string };
}

export const Upcoming = () => {
  const navigate = useNavigate();
  const { eventTracking } = useEventTracking();
  const { isLoading, data } = useUpcomingMatchesQuery();

  const handleCardClick = (match: MatchData) => {
    const eventName =
      match.teamA?.name && match.teamB?.name
        ? `${match.teamA.name} vs ${match.teamB.name}`
        : match.title || match.eventKey;

    eventTracking.event.clicked({
      eventId: match.eventId,
      eventName,
      eventDate: match.date ?? null,
      venue: match.stadium ?? undefined,
      source: 'matches_page',
    });
    navigate('/event/' + match.eventKey);
  };

  return (
    <Box
      as='section'
      bg='BG100'
      px={{ base: 4, md: 6 }}
      py={{ base: 6, md: 8 }}
      color='dark.D900'
      textAlign={{ base: 'center', md: 'right' }}
      aria-labelledby='headingUpcoming'
      width='100vw'
    >
      <VStack width='100%' justifyContent='center' alignItems='center' spacing={6}>
        <Heading
          as='h2'
          id='headingUpcoming'
          mb={{ base: 6, md: 8 }}
          variant={{ base: 'headingMedium', md: 'headingLarge' }}
          textAlign={{ base: 'center', lg: 'right' }}
        >
          <FormattedMessage defaultMessage='Matches' id='homepage.pages.matches' />
        </Heading>
        {isLoading ? (
          <Flex align='center' justify='center' py={12}>
            <Spinner color='green.G50' size='xl' thickness='4px' />
          </Flex>
        ) : data?.items.length ? (
          <Box width='100%' display='flex' justifyContent='center' alignItems='center'>
            <Grid
              templateColumns={{
                base: 'repeat(1, minmax(0, 1fr))',
                md: 'repeat(2, minmax(0, 1fr))',
                lg: 'repeat(3, minmax(0, 1fr))',
              }}
              gap={{ base: 4, md: 6 }}
              justifyContent='center'
              as='ul'
              listStyleType='none'
              width='100%'
              maxW='1200px'
              dir='rtl'
            >
              {data.items.map((match) => (
                <Box as='li' key={match.id} minW='0' width='100%' dir='ltr'>
                  <GameCard {...match} onCardClick={() => handleCardClick(match)} />
                </Box>
              ))}
            </Grid>
          </Box>
        ) : (
          <Flex align='center' justify='center' py={12}>
            <FormattedMessage
              defaultMessage='No upcoming matches'
              id='homepage.upcoming.nomatches'
            />
          </Flex>
        )}
      </VStack>
    </Box>
  );
};
